import { useAnalytics } from '#~/analytics/hooks/index.js';
import {
  createDeviceLifecycleTracker,
  isEmbeddedDiscovery,
  resolveEmbeddedFlowProps
} from '#~/analytics/domains/index.js';
import { CameraModelTooltip } from '#~/common/tooltip/camera-model.jsx';
import { Brand, Model } from '#~/device/components/config/index.js';
import { useBrands, useModels, useTemplate } from '#~/device/registry/hooks.js';
import { Column, Group } from '@studio/design-system/form';
import { Mediator } from '@studio/design-system/input';
import type { HtmlAttributes } from '@studio/design-system/props';
import type { Brand as M_Brand, Model as M_Model } from '@studio/models/device';
import { toAccessor } from '@studio/react-utils';
import { useLatestFn, useShallowEffect } from '@studio/react-utils/hooks';
import { toString } from '@tryiris-ai/js-types';
import { shallowEqual } from 'fast-equals';
import { pick } from 'lodash-es';
import type { ReactElement, SyntheticEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { Opaque, RequireAtLeastOne } from 'type-fest';
import { shallow } from 'zustand/shallow';
import { useProducer, useStore } from '../store/hooks.js';
import type { Selection } from './types.js';
import { getDefaultName, getInitialSelection } from './utils.js';

/** Manufacturer `<Details>` component. */
export const Details = (props: HtmlAttributes): ReactElement => {
  const producer = useProducer();
  const { track } = useAnalytics();
  const deviceLifecycleTracker = createDeviceLifecycleTracker(track);

  const store = useStore(
    (µ) => ({
      ...pick(µ.candidate, ['modelId', 'name']),
      ...pick(µ.manufacturer, ['defaultName', 'preferredModelId'])
    }),
    shallow
  );

  // Per ENG-3960 §1.7 — the embedded flow reuses these shared brand/model select
  // emits, so stamp the additive is_embedded + linking_surface cohort when the
  // in-flight discovery is embedded ({} otherwise — no double-fire, generic unchanged).
  const sourceSelection = useStore((µ) => µ.source.selection);
  const embeddedFlowProps = resolveEmbeddedFlowProps(isEmbeddedDiscovery(sourceSelection));

  const config = useTemplate(store.modelId);

  const [selection, setSelection] = useState<Selection>(getInitialSelection(config));

  const brands = useBrands();
  const models = useModels(selection.brandId);

  const syncSelection = useLatestFn((µ: RequireAtLeastOne<Selection>) => {
    const nextSelection = { ...selection, ...µ };

    if (nextSelection.brandId === 'none') {
      nextSelection.brandId = undefined;
    }

    if (nextSelection.modelId === 'none') {
      nextSelection.modelId = undefined;
    }

    // Did the selection actually change?
    if (shallowEqual(selection, nextSelection)) {
      return;
    }

    setSelection(nextSelection);

    producer((draft) => {
      draft.candidate.modelId = nextSelection.modelId;

      // if we have control settings for this model, we need to clear them.
      draft.candidate.control = undefined;
    });
  });

  // React to changes in the configuration.
  useEffect(() => {
    producer(({ manufacturer }) => {
      manufacturer.config = config;
    });
  }, [config]);

  // React to changes to the selection.
  useShallowEffect(() => {
    producer(({ manufacturer }) => {
      // Keep track of the default device name.
      manufacturer.defaultName = getDefaultName({ brands, models, selection });

      // Keep track of the preferred model for each brand.
      if (selection.brandId && selection.modelId) {
        manufacturer.preferredModelId[selection.brandId] = selection.modelId;
      }
    });
  }, [selection, brands, models]);

  const handleBrandChange = useLatestFn((brandId: M_Brand['id']) => {
    syncSelection({ brandId, modelId: store.preferredModelId[brandId] });

    // Track brand selection
    const selectedBrand = brands?.items?.[brandId];
    if (selectedBrand) {
      // Per ENG-3960 §E12 — device_brand is off-convention; use canonical brand_name.
      deviceLifecycleTracker.trackDeviceSelectBrand({
        brand_name: selectedBrand.name,
        device_brand_id: brandId.toString(),
        ...embeddedFlowProps
      });
    }
  });

  const handleModelChange = useLatestFn((modelId: M_Model['id']) => {
    syncSelection({ modelId });

    // Track model selection
    const selectedModel = models?.items?.[modelId];
    const selectedBrand = selection.brandId ? brands?.items?.[selection.brandId] : undefined;
    if (selectedModel) {
      // Per ENG-3960 §E12 — device_model/device_brand are off-convention; use canonical *_name.
      deviceLifecycleTracker.trackDeviceSelectModel({
        model_name: selectedModel.name,
        device_model_id: modelId.toString(),
        ...(selection.brandId ? { device_brand_id: selection.brandId.toString() } : {}),
        ...(selectedBrand?.name ? { brand_name: selectedBrand.name } : {}),
        ...embeddedFlowProps
      });
    }
  });

  const handleNameChange = useLatestFn((nextValue: string, event: SyntheticEvent) => {
    const nextName = toString(nextValue);

    if (nextName && nextName !== store.name) {
      producer((draft) => {
        draft.candidate.name = nextName;
      });
    } else {
      event.preventDefault();
    }
  });
  const selectedBrand = Object.entries(brands?.items ?? {})?.find?.(
    ([, { id }]) => id === selection.brandId
  );
  const isOtherBrandSelected = selectedBrand?.[1]?.name.toLowerCase().includes('other');

  useEffect(() => {
    if (!isOtherBrandSelected) {
      return;
    }

    const otherModel = Object.entries(models?.items ?? {})
      .map(([, model]) => model)
      ?.find(({ name }) => name.toLowerCase().includes('other'));

    if (otherModel?.id) {
      handleModelChange(otherModel.id);
    }
  }, [isOtherBrandSelected, models, brands]);

  const memoizedSelection = useMemo(
    () => toAccessor(selection.brandId, handleBrandChange),
    [selection.brandId, handleBrandChange]
  );

  const memoizedModelSelection = useMemo(
    () => toAccessor(selection.modelId as Opaque<string, '__deviceModelId'>, handleModelChange),
    [selection.modelId, handleModelChange]
  );

  return (
    <Column {...props}>
      <Group label='Camera brand'>
        <Brand brands={brands} selection={memoizedSelection} />
      </Group>

      {(selectedBrand ? !isOtherBrandSelected : true) && (
        <Group label='Camera model' annotation={<CameraModelTooltip />}>
          <Model
            key={selection.brandId}
            models={models}
            selection={memoizedModelSelection}
            brandId={selection.brandId}
          />
        </Group>
      )}

      <Group label='Camera nickname'>
        <Mediator onCommit={handleNameChange} placeholder={store.defaultName} value={store.name} />
      </Group>
    </Column>
  );
};
